import { useEffect, useRef, useState } from 'react';

interface NeuralHeartSynapseProps {
  onInteract?: () => void;
}

// --- 3D Math & Quaternion Utilities ---
type Vec3 = [number, number, number];
type Quat = [number, number, number, number]; // [x, y, z, w]

function normalize3(v: Vec3): Vec3 {
  const len = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / len, v[1] / len, v[2] / len];
}

function cross3(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function dot3(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function sub3(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function add3(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

function scale3(a: Vec3, s: number): Vec3 {
  return [a[0] * s, a[1] * s, a[2] * s];
}

function quatFromAxisAngle(axis: Vec3, rad: number): Quat {
  const half = rad * 0.5;
  const s = Math.sin(half);
  return [axis[0] * s, axis[1] * s, axis[2] * s, Math.cos(half)];
}

function quatMultiply(a: Quat, b: Quat): Quat {
  return [
    a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
    a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
    a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
    a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
  ];
}

function quatNormalize(q: Quat): Quat {
  const len = Math.hypot(q[0], q[1], q[2], q[3]) || 1;
  return [q[0] / len, q[1] / len, q[2] / len, q[3] / len];
}

function quatToMat4(q: Quat): Float32Array {
  const [x, y, z, w] = q;
  const x2 = x + x, y2 = y + y, z2 = z + z;
  const xx = x * x2, xy = x * y2, xz = x * z2;
  const yy = y * y2, yz = y * z2, zz = z * z2;
  const wx = w * x2, wy = w * y2, wz = w * z2;

  return new Float32Array([
    1 - (yy + zz), xy + wz, xz - wy, 0,
    xy - wz, 1 - (xx + zz), yz + wx, 0,
    xz + wy, yz - wx, 1 - (xx + yy), 0,
    0, 0, 0, 1,
  ]);
}

function mat4Perspective(fovRad: number, aspect: number, near: number, far: number): Float32Array {
  const f = 1.0 / Math.tan(fovRad * 0.5);
  const nf = 1.0 / (near - far);
  return new Float32Array([
    f / aspect, 0, 0, 0,
    0, f, 0, 0,
    0, 0, (far + near) * nf, -1,
    0, 0, 2 * far * near * nf, 0,
  ]);
}

// Deterministic pseudo-random generator for reproducible lush organic geometry
function createPRNG(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Smooth 3D curl-like organic field for chaotic dendrite/axon growth
function chaoticField(x: number, y: number, z: number, seedOffset: number): Vec3 {
  const s1 = seedOffset * 1.73;
  const s2 = seedOffset * 2.41;
  const s3 = seedOffset * 3.19;

  const nx =
    Math.sin(y * 1.9 + s1) * Math.cos(z * 1.6 + s2) +
    0.55 * Math.cos(z * 3.3 - x * 2.1 + s3) +
    0.25 * Math.sin(x * 5.2 + y * 4.7 - s1);
  const ny =
    Math.sin(z * 1.8 + s2) * Math.cos(x * 1.7 + s3) +
    0.55 * Math.cos(x * 3.1 - y * 2.3 + s1) +
    0.25 * Math.sin(y * 5.0 + z * 4.5 - s2);
  const nz =
    Math.sin(x * 2.0 + s3) * Math.cos(y * 1.5 + s1) +
    0.55 * Math.cos(y * 3.4 - z * 2.0 + s2) +
    0.25 * Math.sin(z * 4.9 + x * 4.6 - s3);

  return [nx, ny, nz];
}

// --- 3D Parametric Human Heart Soma Evaluation ---
export interface HeartGraphNode {
  pos: Vec3;
  normal: Vec3;
  isVessel: boolean;
  isNeuronOrigin: boolean;
}

// Evaluate a point on the asymmetrical 3D anatomical human heart chamber (ventricles + atria)
// u in [0, 1]: 0 = inferior cardiac apex (tilted down-right-forward), 1 = superior atrial base
// phi in [0, 2*PI): azimuth around the oblique anatomical axis
function evalAnatomicalChamberPoint(u: number, phi: number): Vec3 {
  const cosPhi = Math.cos(phi);
  const sinPhi = Math.sin(phi);

  // Oblique ~42° anatomical axis: apex at (+0.52, -0.90, +0.18), superior base at (-0.18, +0.42, -0.04)
  const y = -0.90 + 1.32 * Math.pow(u, 0.90);
  const axisX = 0.52 * Math.pow(1.0 - u, 1.22) - 0.18 * Math.pow(u, 1.05);
  const axisZ = 0.18 * Math.pow(1.0 - u, 1.30) - 0.04 * u;

  // Broad muscular conical-ovoid envelope: pointed ventricular apex at u=0, wide mid-chambers, domed atrial base at u=1
  const tipProfile = Math.pow(Math.sin(Math.min(1.0, u) * Math.PI * 0.88), 0.56);
  const topCap = Math.pow(Math.max(0.0, 1.0 - Math.pow(u, 4.4)), 0.52);
  let r = 0.78 * tipProfile * topCap;

  // Coronary sulcus (atrioventricular groove separating ventricles u < 0.64 from atria u > 0.64)
  const coronarySulcus = 1.0 - 0.12 * Math.exp(-Math.pow((u - 0.64) / 0.075, 2.0));
  r *= coronarySulcus;

  // Anterior & posterior interventricular sulcus (diagonal groove separating right & left ventricles)
  const antSulcusAngle = 0.42 + (1.0 - u) * 0.58;
  const dAnt = Math.atan2(Math.sin(phi - antSulcusAngle), Math.cos(phi - antSulcusAngle));
  const postSulcusAngle = -1.75 + (1.0 - u) * 0.35;
  const dPost = Math.atan2(Math.sin(phi - postSulcusAngle), Math.cos(phi - postSulcusAngle));
  const interventricularGroove =
    1.0 -
    0.13 * Math.exp(-Math.pow(dAnt / 0.25, 2.0)) * Math.sin(u * Math.PI) -
    0.09 * Math.exp(-Math.pow(dPost / 0.28, 2.0)) * Math.sin(u * Math.PI);
  r *= interventricularGroove;

  // Chamber asymmetry:
  // - Viewer's left (cosPhi < 0, anatomical right): prominent Right Atrium lateral bulge + Right Ventricle
  // - Viewer's right (cosPhi > 0, anatomical left): thick convex muscular Left Ventricle wall & Left Auricle
  const rightAtriumBulge =
    cosPhi < 0.0
      ? 1.0 + 0.30 * Math.pow(-cosPhi, 1.15) * Math.exp(-Math.pow((u - 0.62) / 0.28, 2.0))
      : 1.0;
  const leftVentricleWall =
    cosPhi > 0.0
      ? 1.0 + 0.22 * Math.pow(cosPhi, 1.05) * Math.exp(-Math.pow((u - 0.42) / 0.32, 2.0))
      : 1.0;

  const rx = r * rightAtriumBulge * leftVentricleWall;
  // Real human heart is slightly compressed front-to-back with a fuller anterior sternocostal surface
  const anteriorFullness = sinPhi > 0.0 ? 0.80 : 0.68;
  const rz = r * anteriorFullness;

  const x = axisX + rx * cosPhi;
  const z = axisZ + rz * sinPhi;

  return [x, y, z];
}

// --- Build 3D Anatomical Human Heart Out of Planes Connected Like a 3D Graph ---
function buildAnatomicalHeartGraph() {
  const rand = createPRNG(13579246);
  const graphNodes: HeartGraphNode[] = [];
  const faces: [number, number, number][] = [];

  const addNode = (pos: Vec3, normal: Vec3, isVessel: boolean): number => {
    const idx = graphNodes.length;
    graphNodes.push({
      pos,
      normal: normalize3(normal),
      isVessel,
      isNeuronOrigin: false,
    });
    return idx;
  };

  // 1. Build Main Faceted Anatomical Chamber (Ventricles + Atria) as a Staggered Triangulated Planar Graph
  const latRings = 11;
  const lonCount = 14;

  // Apex graph node (u = 0)
  const apexPos = evalAnatomicalChamberPoint(0.0, 0.0);
  const apexNodeIdx = addNode(apexPos, [0.45, -0.88, 0.2], false);

  const ringNodeIndices: number[][] = [];

  for (let i = 1; i < latRings; i++) {
    const uBase = i / latRings;
    const ring: number[] = [];
    const stagger = (i % 2) * 0.5;

    for (let j = 0; j < lonCount; j++) {
      // Slight deterministic graph node jitter so triangular planes look like an engineered 3D polyhedral graph
      const uJitter = uBase + (rand() - 0.5) * 0.022;
      const phi = ((j + stagger + (rand() - 0.5) * 0.14) / lonCount) * Math.PI * 2.0;

      const pos = evalAnatomicalChamberPoint(uJitter, phi);
      const pU = evalAnatomicalChamberPoint(Math.min(0.99, uJitter + 0.02), phi);
      const pP = evalAnatomicalChamberPoint(uJitter, phi + 0.05);
      let norm = cross3(sub3(pP, pos), sub3(pU, pos));
      if (dot3(norm, pos) < 0) norm = scale3(norm, -1);

      ring.push(addNode(pos, norm, false));
    }
    ringNodeIndices.push(ring);
  }

  // Superior base center node (u = 1)
  const baseCapPos = evalAnatomicalChamberPoint(1.0, 0.0);
  const baseCapIdx = addNode(baseCapPos, [-0.15, 0.98, 0.0], false);

  // Connect Apex node to first ring with triangular planes
  const firstRing = ringNodeIndices[0];
  for (let j = 0; j < lonCount; j++) {
    const nextJ = (j + 1) % lonCount;
    faces.push([apexNodeIdx, firstRing[nextJ], firstRing[j]]);
  }

  // Connect intermediate staggered rings with interlocking triangular planes
  for (let r = 0; r < ringNodeIndices.length - 1; r++) {
    const r0 = ringNodeIndices[r];
    const r1 = ringNodeIndices[r + 1];
    const isEven = r % 2 === 0;

    for (let j = 0; j < lonCount; j++) {
      const nextJ = (j + 1) % lonCount;
      const a = r0[j];
      const b = r0[nextJ];
      const c = r1[j];
      const d = r1[nextJ];

      if (isEven) {
        faces.push([a, b, c]);
        faces.push([b, d, c]);
      } else {
        faces.push([a, d, c]);
        faces.push([a, b, d]);
      }
    }
  }

  // Connect top ring to superior base node
  const lastRing = ringNodeIndices[ringNodeIndices.length - 1];
  for (let j = 0; j < lonCount; j++) {
    const nextJ = (j + 1) % lonCount;
    faces.push([lastRing[j], lastRing[nextJ], baseCapIdx]);
  }

  // 2. Helper to build Faceted Polyhedral Great Vessels (Aortic Arch, SVC, Pulmonary Trunk, Arterial Branches)
  const addPolyhedralVessel = (spine: Vec3[], radii: number[], sides: number, capEnd = true) => {
    const nRings = spine.length;
    if (nRings < 2) return;

    const tangents: Vec3[] = [];
    for (let i = 0; i < nRings; i++) {
      if (i === 0) tangents.push(normalize3(sub3(spine[1], spine[0])));
      else if (i === nRings - 1) tangents.push(normalize3(sub3(spine[nRings - 1], spine[nRings - 2])));
      else tangents.push(normalize3(sub3(spine[i + 1], spine[i - 1])));
    }

    const t0 = tangents[0];
    const upCand: Vec3 = Math.abs(t0[2]) < 0.85 ? [0, 0, 1] : [1, 0, 0];
    let frameN = normalize3(cross3(t0, upCand));
    let frameB = normalize3(cross3(t0, frameN));

    const vRings: number[][] = [];

    for (let i = 0; i < nRings; i++) {
      if (i > 0) {
        const prevT = tangents[i - 1];
        const currT = tangents[i];
        const axis = cross3(prevT, currT);
        const axisLen = Math.hypot(axis[0], axis[1], axis[2]);
        if (axisLen > 1e-5) {
          const uAxis = scale3(axis, 1.0 / axisLen);
          const ang = Math.acos(Math.max(-1, Math.min(1, dot3(prevT, currT))));
          const cA = Math.cos(ang);
          const sA = Math.sin(ang);
          const cr = cross3(uAxis, frameN);
          const dt = dot3(uAxis, frameN);
          frameN = normalize3([
            frameN[0] * cA + cr[0] * sA + uAxis[0] * dt * (1 - cA),
            frameN[1] * cA + cr[1] * sA + uAxis[1] * dt * (1 - cA),
            frameN[2] * cA + cr[2] * sA + uAxis[2] * dt * (1 - cA),
          ]);
          frameB = normalize3(cross3(currT, frameN));
        }
      }

      const ring: number[] = [];
      const stagger = (i % 2) * 0.5;
      const rad = radii[i];
      const center = spine[i];

      for (let s = 0; s < sides; s++) {
        const theta = ((s + stagger) / sides) * Math.PI * 2.0;
        const cosTh = Math.cos(theta);
        const sinTh = Math.sin(theta);
        const norm: Vec3 = normalize3([
          frameN[0] * cosTh + frameB[0] * sinTh,
          frameN[1] * cosTh + frameB[1] * sinTh,
          frameN[2] * cosTh + frameB[2] * sinTh,
        ]);
        const pos = add3(center, scale3(norm, rad));
        ring.push(addNode(pos, norm, i < nRings - 1));
      }
      vRings.push(ring);
    }

    for (let i = 0; i < nRings - 1; i++) {
      const r0 = vRings[i];
      const r1 = vRings[i + 1];
      for (let s = 0; s < sides; s++) {
        const nextS = (s + 1) % sides;
        faces.push([r0[s], r0[nextS], r1[s]]);
        faces.push([r0[nextS], r1[nextS], r1[s]]);
      }
    }

    if (capEnd) {
      const lastRing = vRings[nRings - 1];
      const endCenter = spine[nRings - 1];
      const endTangent = tangents[nRings - 1];
      const capIdx = addNode(add3(endCenter, scale3(endTangent, radii[nRings - 1] * 0.25)), endTangent, false);
      for (let s = 0; s < sides; s++) {
        const nextS = (s + 1) % sides;
        faces.push([lastRing[s], lastRing[nextS], capIdx]);
      }
    }
  };

  // A. Ascending Aorta & Aortic Arch (prominent central vessel arching up and to the right-back)
  addPolyhedralVessel(
    [
      [-0.06, 0.26, 0.02],
      [-0.10, 0.58, 0.05],
      [-0.04, 0.84, 0.04],
      [0.12, 0.98, -0.01],
      [0.32, 0.90, -0.09],
      [0.42, 0.64, -0.18],
    ],
    [0.21, 0.21, 0.20, 0.19, 0.18, 0.17],
    7,
    true
  );

  // B. Three Superior Arterial Branches off the Aortic Arch
  // 1) Brachiocephalic trunk
  addPolyhedralVessel(
    [
      [-0.04, 0.90, 0.03],
      [-0.09, 1.12, 0.04],
      [-0.13, 1.28, 0.04],
    ],
    [0.082, 0.075, 0.068],
    5,
    true
  );
  // 2) Left common carotid artery
  addPolyhedralVessel(
    [
      [0.10, 0.96, -0.01],
      [0.11, 1.16, -0.01],
      [0.12, 1.32, -0.02],
    ],
    [0.072, 0.066, 0.060],
    5,
    true
  );
  // 3) Left subclavian artery
  addPolyhedralVessel(
    [
      [0.25, 0.91, -0.06],
      [0.29, 1.10, -0.07],
      [0.33, 1.25, -0.08],
    ],
    [0.070, 0.064, 0.058],
    5,
    true
  );

  // C. Superior Vena Cava (vertical great vessel entering top of Right Atrium on viewer's left)
  addPolyhedralVessel(
    [
      [-0.52, 0.22, -0.04],
      [-0.56, 0.54, -0.05],
      [-0.58, 0.82, -0.05],
      [-0.60, 1.06, -0.06],
    ],
    [0.165, 0.16, 0.15, 0.145],
    6,
    true
  );

  // D. Pulmonary Trunk / Artery (crossing diagonally upward & right across the anterior upper heart)
  addPolyhedralVessel(
    [
      [-0.08, 0.18, 0.24],
      [0.08, 0.46, 0.26],
      [0.28, 0.62, 0.20],
      [0.52, 0.68, 0.12],
    ],
    [0.175, 0.17, 0.16, 0.15],
    6,
    true
  );

  // 3. Emit Non-Indexed Flat Triangular Planes with Barycentric Coordinates
  // Vertex layout: aPos(3), aNormal(3), aBary(3), aPlaneMeta(2: planeHash, nodeHeight) = 11 floats
  const numTris = faces.length;
  const verts = new Float32Array(numTris * 3 * 11);
  let ptr = 0;

  for (let f = 0; f < numTris; f++) {
    const [i0, i1, i2] = faces[f];
    const p0 = graphNodes[i0].pos;
    const p1 = graphNodes[i1].pos;
    const p2 = graphNodes[i2].pos;

    // Exact geometric flat plane normal so each face is rendered as a crisp flat polygon plane
    const e1 = sub3(p1, p0);
    const e2 = sub3(p2, p0);
    let planeNorm = normalize3(cross3(e1, e2));
    const centroid: Vec3 = [
      (p0[0] + p1[0] + p2[0]) / 3.0,
      (p0[1] + p1[1] + p2[1]) / 3.0,
      (p0[2] + p1[2] + p2[2]) / 3.0,
    ];
    const avgNodeNorm = add3(graphNodes[i0].normal, add3(graphNodes[i1].normal, graphNodes[i2].normal));
    if (dot3(planeNorm, avgNodeNorm) < 0) {
      planeNorm = scale3(planeNorm, -1.0);
    }

    const planeHash = ((f * 2654435761) >>> 0) / 0xffffffff;
    const planePts: [Vec3, Vec3][] = [
      [p0, [1, 0, 0]],
      [p1, [0, 1, 0]],
      [p2, [0, 0, 1]],
    ];

    for (const [pt, bary] of planePts) {
      verts[ptr++] = pt[0];
      verts[ptr++] = pt[1];
      verts[ptr++] = pt[2];
      verts[ptr++] = planeNorm[0];
      verts[ptr++] = planeNorm[1];
      verts[ptr++] = planeNorm[2];
      verts[ptr++] = bary[0];
      verts[ptr++] = bary[1];
      verts[ptr++] = bary[2];
      verts[ptr++] = planeHash;
      verts[ptr++] = centroid[1];
    }
  }

  return {
    verts,
    vertexCount: numTris * 3,
    graphNodes,
  };
}

interface SynapticNodeData {
  pos: Vec3;
  u: number;
  hue: number;
  neuronId: number;
  size: number;
}

// --- Build Several Dozen Chaotic 3D Neurons Originating Out of the 3D Heart Graph Nodes ---
function buildChaoticNeuronsMesh(heartNodes: HeartGraphNode[]) {
  const rand = createPRNG(20261017);
  const radialSides = 7;

  // Filter candidate graph nodes: prefer lateral/perimeter nodes of the anatomical heart graph
  // so neurons sprout visibly from the 3D graph nodes without crisscrossing directly across the front face
  const candidateIndices: number[] = [];
  for (let idx = 0; idx < heartNodes.length; idx++) {
    const node = heartNodes[idx];
    if (!node.isVessel && Math.abs(node.normal[2]) < 0.76) {
      candidateIndices.push(idx);
    }
  }

  const primaryCount = Math.min(54, candidateIndices.length);

  // Vertex layout:
  // aPos(3), aCenter(3), aNormal(3), aMeta(4: u, hue, neuronId, radius) = 13 floats
  const vertList: number[] = [];
  const indexList: number[] = [];
  const synapticNodes: SynapticNodeData[] = [];

  let currentVertexOffset = 0;

  // Helper to extrude a smooth 3D tube along a spline curve using Parallel Transport Frames
  const extrudeSplineTube = (
    points: Vec3[],
    uValues: number[],
    radii: number[],
    hue: number,
    neuronId: number
  ) => {
    const nPts = points.length;
    if (nPts < 2) return;

    const tangents: Vec3[] = [];
    for (let i = 0; i < nPts; i++) {
      if (i === 0) {
        tangents.push(normalize3(sub3(points[1], points[0])));
      } else if (i === nPts - 1) {
        tangents.push(normalize3(sub3(points[nPts - 1], points[nPts - 2])));
      } else {
        tangents.push(normalize3(sub3(points[i + 1], points[i - 1])));
      }
    }

    // Initial frame normal perpendicular to tangents[0]
    const t0 = tangents[0];
    const upCandidate: Vec3 = Math.abs(t0[1]) < 0.85 ? [0, 1, 0] : [1, 0, 0];
    let frameN = normalize3(cross3(t0, upCandidate));
    let frameB = normalize3(cross3(t0, frameN));

    const baseVert = currentVertexOffset;

    for (let i = 0; i < nPts; i++) {
      if (i > 0) {
        // Parallel transport frame rotation
        const prevT = tangents[i - 1];
        const currT = tangents[i];
        const axis = cross3(prevT, currT);
        const axisLen = Math.hypot(axis[0], axis[1], axis[2]);
        if (axisLen > 1e-5) {
          const unitAxis = scale3(axis, 1.0 / axisLen);
          const angle = Math.acos(Math.max(-1, Math.min(1, dot3(prevT, currT))));
          const cosA = Math.cos(angle);
          const sinA = Math.sin(angle);
          const crossAN = cross3(unitAxis, frameN);
          const dotAN = dot3(unitAxis, frameN);
          frameN = normalize3([
            frameN[0] * cosA + crossAN[0] * sinA + unitAxis[0] * dotAN * (1 - cosA),
            frameN[1] * cosA + crossAN[1] * sinA + unitAxis[1] * dotAN * (1 - cosA),
            frameN[2] * cosA + crossAN[2] * sinA + unitAxis[2] * dotAN * (1 - cosA),
          ]);
          frameB = normalize3(cross3(currT, frameN));
        }
      }

      const center = points[i];
      const r = radii[i];
      const u = uValues[i];

      for (let s = 0; s < radialSides; s++) {
        const theta = (s / radialSides) * Math.PI * 2.0;
        const cosTh = Math.cos(theta);
        const sinTh = Math.sin(theta);

        const norm: Vec3 = normalize3([
          frameN[0] * cosTh + frameB[0] * sinTh,
          frameN[1] * cosTh + frameB[1] * sinTh,
          frameN[2] * cosTh + frameB[2] * sinTh,
        ]);

        const pos: Vec3 = [
          center[0] + norm[0] * r,
          center[1] + norm[1] * r,
          center[2] + norm[2] * r,
        ];

        vertList.push(
          pos[0], pos[1], pos[2],
          center[0], center[1], center[2],
          norm[0], norm[1], norm[2],
          u, hue, neuronId, r
        );
        currentVertexOffset++;
      }
    }

    for (let i = 0; i < nPts - 1; i++) {
      const ring0 = baseVert + i * radialSides;
      const ring1 = baseVert + (i + 1) * radialSides;
      for (let s = 0; s < radialSides; s++) {
        const nextS = (s + 1) % radialSides;
        const a = ring0 + s;
        const b = ring1 + s;
        const c = ring1 + nextS;
        const d = ring0 + nextS;

        indexList.push(a, b, d);
        indexList.push(b, c, d);
      }
    }
  };

  // Select well-distributed 3D Graph Nodes from the anatomical heart graph as the exact origins of the neurons!
  const stride = Math.max(1, Math.floor(candidateIndices.length / primaryCount));

  for (let n = 0; n < primaryCount; n++) {
    const neuronId = (n + 0.5) / primaryCount;
    // Full rainbow distribution with golden ratio scrambling so neighboring neurons contrast vividly
    const hue = (n * 0.61803398875) % 1.0;

    const nodeIdx = candidateIndices[(n * stride + Math.floor(n * 0.37)) % candidateIndices.length];
    const originNode = heartNodes[nodeIdx];
    originNode.isNeuronOrigin = true;

    const surfPos = originNode.pos;
    const surfNorm = originNode.normal;

    const numSteps = 36;
    const totalLength = 1.55 + rand() * 1.85; // Chaotic varied reach in 3D space
    const stepLen = totalLength / numSteps;
    const baseRadius = 0.018 + rand() * 0.012;
    const chaosSeed = n * 13.37 + rand() * 7.0;
    const curlWeight = 0.52 + rand() * 0.48;

    const points: Vec3[] = [];
    const uValues: number[] = [];
    const radii: number[] = [];

    // Exact origin at the 3D Graph Node of the human heart!
    let currPos: Vec3 = [surfPos[0], surfPos[1], surfPos[2]];
    const outwardInit = normalize3(add3(surfNorm, scale3(normalize3(surfPos), 0.65)));
    let currDir: Vec3 = normalize3([
      outwardInit[0] + (rand() - 0.5) * 0.18,
      outwardInit[1] + (rand() - 0.5) * 0.18,
      outwardInit[2] + (rand() - 0.5) * 0.18,
    ]);

    for (let i = 0; i <= numSteps; i++) {
      const u = i / numSteps;
      points.push([currPos[0], currPos[1], currPos[2]]);
      uValues.push(u);

      // Smooth taper from the 3D graph node outward, with slight synaptic bulb at tip
      const nodeCollar = Math.exp(-u * 18.0) * 0.012;
      const taper = baseRadius * (1.0 - 0.74 * Math.pow(u, 0.75));
      const tipBulb = Math.exp(-Math.pow((u - 1.0) * 18.0, 2.0)) * 0.009;
      radii.push(nodeCollar + taper + tipBulb);

      // Evolve chaotic 3D direction using curl-like field + outward radial bias
      const field = chaoticField(currPos[0] * 0.95, currPos[1] * 0.95, currPos[2] * 0.95, chaosSeed + u * 2.2);
      const radialOut = normalize3(currPos);

      // Blend outward momentum with chaotic 3D twisting & spiraling once clear of the heart surface
      const swirlWeight = Math.pow(Math.min(1.0, u * 1.85), 1.35) * curlWeight;
      const radialPull = 0.34 * (1.0 - 0.35 * u);
      currDir = normalize3([
        currDir[0] * 0.56 + radialOut[0] * radialPull + field[0] * swirlWeight,
        currDir[1] * 0.56 + radialOut[1] * radialPull + field[1] * swirlWeight,
        currDir[2] * 0.56 + radialOut[2] * radialPull + field[2] * swirlWeight,
      ]);

      currPos = add3(currPos, scale3(currDir, stepLen));

      // Place intermediate synaptic varicosity node occasionally
      if (i === Math.floor(numSteps * 0.55) && rand() < 0.65) {
        synapticNodes.push({
          pos: [currPos[0], currPos[1], currPos[2]],
          u,
          hue,
          neuronId,
          size: 11.0 + rand() * 6.0,
        });
      }
    }

    extrudeSplineTube(points, uValues, radii, hue, neuronId);

    // Terminal synaptic bulb at the end of every primary neuron
    const tipPos = points[points.length - 1];
    synapticNodes.push({
      pos: tipPos,
      u: 1.0,
      hue,
      neuronId,
      size: 14.0 + rand() * 8.0,
    });

    // Spawn 1 or 2 chaotic secondary dendritic branches splitting off the primary neuron
    const numBranches = rand() < 0.78 ? (rand() < 0.35 ? 2 : 1) : 0;
    for (let b = 0; b < numBranches; b++) {
      const splitIdx = Math.floor((0.28 + b * 0.26 + rand() * 0.18) * numSteps);
      if (splitIdx >= points.length - 3) continue;

      const bSteps = 18;
      const bPoints: Vec3[] = [];
      const bUValues: number[] = [];
      const bRadii: number[] = [];

      let bPos: Vec3 = [...points[splitIdx]];
      const parentU = uValues[splitIdx];
      const parentR = radii[splitIdx] * 0.75;
      const parentTangent = normalize3(sub3(points[splitIdx + 1], points[splitIdx - 1]));

      // Fork off at an organic 3D angle
      const randVec: Vec3 = normalize3([rand() - 0.5, rand() - 0.5, rand() - 0.5]);
      const perpVec = normalize3(cross3(parentTangent, randVec));
      let bDir: Vec3 = normalize3(add3(scale3(parentTangent, 0.65), scale3(perpVec, 0.75)));
      const bStepLen = stepLen * (0.75 + rand() * 0.35);
      const bSeed = chaosSeed + 31.7 * (b + 1);
      const bHue = (hue + 0.08 * (b + 1)) % 1.0;

      for (let k = 0; k <= bSteps; k++) {
        const frac = k / bSteps;
        const u = parentU + frac * (1.0 - parentU);
        bPoints.push([bPos[0], bPos[1], bPos[2]]);
        bUValues.push(u);
        bRadii.push(parentR * (1.0 - 0.76 * Math.pow(frac, 0.8)));

        const field = chaoticField(bPos[0] * 1.2, bPos[1] * 1.2, bPos[2] * 1.2, bSeed + frac * 2.5);
        bDir = normalize3([
          bDir[0] * 0.62 + field[0] * 0.48,
          bDir[1] * 0.62 + field[1] * 0.48,
          bDir[2] * 0.62 + field[2] * 0.48,
        ]);
        bPos = add3(bPos, scale3(bDir, bStepLen));
      }

      extrudeSplineTube(bPoints, bUValues, bRadii, bHue, neuronId);

      // Terminal bouton on secondary branch
      const bTip = bPoints[bPoints.length - 1];
      synapticNodes.push({
        pos: bTip,
        u: 1.0,
        hue: bHue,
        neuronId,
        size: 11.0 + rand() * 6.0,
      });
    }
  }

  return {
    verts: new Float32Array(vertList),
    indices: new Uint16Array(indexList),
    synapticNodes,
  };
}

// --- Build 3D Heart Graph Nodes + Synaptic Bulbs + Ambient Bioluminescent Spores Buffer ---
function buildParticlesBuffer(synapticNodes: SynapticNodeData[], heartNodes: HeartGraphNode[]) {
  const rand = createPRNG(998877);
  const ambientCount = 160;
  const totalCount = heartNodes.length + synapticNodes.length + ambientCount;

  // Layout per particle: aPos(3), aMeta(4: u, hue, neuronId, sizeAndType) = 7 floats
  // sizeAndType > 100 => 3D Heart Graph Node (neon green #39ff14)
  // 0 < sizeAndType < 100 => attached neuron synaptic bulb (rainbow)
  // sizeAndType < 0 => floating ambient spore
  const data = new Float32Array(totalCount * 7);
  let ptr = 0;

  // 1. 3D Heart Graph Nodes (the vertices where planes connect and neurons originate!)
  for (let i = 0; i < heartNodes.length; i++) {
    const hn = heartNodes[i];
    data[ptr++] = hn.pos[0];
    data[ptr++] = hn.pos[1];
    data[ptr++] = hn.pos[2];
    data[ptr++] = 0.0; // u = 0 (on heart surface)
    data[ptr++] = hn.isNeuronOrigin ? 1.0 : 0.0; // flag for neuron origin hub
    data[ptr++] = (i + 0.5) / heartNodes.length;
    data[ptr++] = 100.0 + (hn.isNeuronOrigin ? 15.5 : 10.5);
  }

  // 2. Neuron Synaptic Nodes / Boutons
  for (const node of synapticNodes) {
    data[ptr++] = node.pos[0];
    data[ptr++] = node.pos[1];
    data[ptr++] = node.pos[2];
    data[ptr++] = node.u;
    data[ptr++] = node.hue;
    data[ptr++] = node.neuronId;
    data[ptr++] = node.size; // positive (11..22) => synaptic node
  }

  // 3. Ambient Deep-Space Spores
  for (let i = 0; i < ambientCount; i++) {
    const u = rand();
    const v = rand();
    const theta = u * 2.0 * Math.PI;
    const phi = Math.acos(2.0 * v - 1.0);
    const radius = 1.25 + Math.pow(rand(), 0.75) * 3.4;

    const x = radius * Math.sin(phi) * Math.cos(theta);
    const y = radius * Math.sin(phi) * Math.sin(theta);
    const z = radius * Math.cos(phi);

    data[ptr++] = x;
    data[ptr++] = y;
    data[ptr++] = z;
    data[ptr++] = rand(); // phase offset
    data[ptr++] = rand(); // rainbow hue
    data[ptr++] = rand(); // drift seed
    data[ptr++] = -(5.5 + rand() * 9.5); // negative => ambient floating spore
  }

  return { data, count: totalCount };
}

// --- WebGL Shader Compilation Helpers ---
function compileShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('Failed to create WebGL shader');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile error: ${info}`);
  }
  return shader;
}

function createProgram(gl: WebGLRenderingContext, vsSource: string, fsSource: string): WebGLProgram {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSource);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSource);
  const prog = gl.createProgram();
  if (!prog) throw new Error('Failed to create WebGL program');
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    const info = gl.getProgramInfoLog(prog);
    throw new Error(`Program link error: ${info}`);
  }
  return prog;
}

// --- Shared GLSL Functions (Heartbeat Kinematics & Rainbow Palette) ---
const GLSL_COMMON = `
precision highp float;

// Rich saturated rainbow palette (Pixar bioluminescence without white blowout)
vec3 rainbowPalette(float t) {
  vec3 a = vec3(0.50, 0.50, 0.52);
  vec3 b = vec3(0.48, 0.48, 0.46);
  vec3 c = vec3(1.0, 1.0, 1.0);
  vec3 d = vec3(0.00, 0.33, 0.67);
  vec3 col = a + b * cos(6.2831853 * (c * t + d));
  return pow(max(col, vec3(0.0)), vec3(1.15)) * 1.05;
}

// Realistic two-stage Lub-Dub cardiac waveform for a given phase in [0, 1)
float lubDubWave(float phase, float excitation) {
  float p = fract(phase);
  // First beat ("Lub" - atrial/early ventricular contraction)
  float lub = exp(-pow((p - 0.11) / 0.048, 2.0));
  // Second beat ("Dub" - peak ventricular ejection & recoil)
  float dub = 1.18 * exp(-pow((p - 0.25) / 0.062, 2.0));
  // Subtle elastic diastolic rebound
  float rebound = -0.18 * exp(-pow((p - 0.39) / 0.085, 2.0));
  float amp = 1.0 + excitation * 0.92;
  return (lub + dub + rebound) * amp;
}

// Deform a 3D point on/near the heart according to the cardiac wave
vec3 deformHeartPoint(vec3 pos, float beatPhase, float excitation, float time) {
  // Wave propagates from top lobes (pos.y > 0) down to apex (pos.y < 0)
  float waveDelay = clamp((0.8 - pos.y) * 0.055, 0.0, 0.14);
  float w = lubDubWave(beatPhase - waveDelay, excitation);

  // Pixar-style volume-preserving squash & stretch + systolic twist
  float radialScale = 1.0 + w * 0.155;
  float verticalScale = 1.0 - w * 0.055 + max(w, 0.0) * 0.08;
  float twistAngle = w * 0.11 * (pos.y + 0.4);

  float c = cos(twistAngle);
  float s = sin(twistAngle);
  vec3 p = vec3(
    pos.x * radialScale,
    pos.y * verticalScale,
    pos.z * radialScale
  );

  // Subtle organic cellular breathing
  float ripple = sin(pos.x * 6.0 + time * 2.2) * cos(pos.y * 6.0 - time * 1.8) * 0.012 * (1.0 + excitation);
  p += normalize(pos + vec3(0.0, 0.001, 0.0)) * ripple;

  return vec3(p.x * c - p.z * s, p.y, p.x * s + p.z * c);
}
`;

// 1. Background Cosmic Bioluminescent Nebula Shader
const BG_VS = `
attribute vec2 aQuadPos;
varying vec2 vUV;
void main() {
  vUV = aQuadPos * 0.5 + 0.5;
  gl_Position = vec4(aQuadPos, 0.0, 1.0);
}
`;

const BG_FS = `
${GLSL_COMMON}
varying vec2 vUV;
uniform float uTime;
uniform float uBeatPhase;
uniform float uExcitation;
uniform vec2 uResolution;

void main() {
  vec2 uv = (vUV - 0.5) * 2.0;
  uv.x *= uResolution.x / max(uResolution.y, 1.0);
  float r = length(uv);

  float beat = max(0.0, lubDubWave(uBeatPhase, uExcitation));

  // Deep velvety midnight backdrop
  vec3 deepBg = mix(vec3(0.005, 0.012, 0.008), vec3(0.002, 0.004, 0.006), smoothstep(0.0, 1.35, r));

  // Subtle atmospheric halo deep in space behind the anatomical heart
  float aura1 = exp(-r * r * (3.6 - 0.8 * uExcitation - 0.3 * beat));
  float aura2 = exp(-r * 2.1) * 0.16;

  vec3 heartAuraCol = mix(
    vec3(0.03, 0.24, 0.06),
    vec3(0.08, 0.48, 0.14),
    uExcitation
  );

  // Subtle expanding heartbeat shockwave ring in deep space
  float waveRadius = fract(uBeatPhase) * 1.35;
  float shockRing = exp(-pow((r - waveRadius) * 10.0, 2.0)) * (1.0 - fract(uBeatPhase)) * (0.04 + 0.14 * uExcitation);
  vec3 ringCol = rainbowPalette(fract(waveRadius * 0.5 - uTime * 0.1));

  vec3 col = deepBg
    + heartAuraCol * aura1 * (0.11 + 0.08 * beat + 0.13 * uExcitation)
    + vec3(0.02, 0.12, 0.06) * aura2
    + ringCol * shockRing;

  gl_FragColor = vec4(col, 1.0);
}
`;

// 2. Central 3D Anatomical Human Heart Shader (Constructed out of Flat Geometric Planes & Connected 3D Graph Edges in Theme Neon Green)
const HEART_VS = `
${GLSL_COMMON}
attribute vec3 aPos;
attribute vec3 aNormal;
attribute vec3 aBary;
attribute vec2 aPlaneMeta; // x: planeHash, y: centroidY

uniform mat4 uProj;
uniform mat4 uRot;
uniform float uCameraZ;
uniform float uTime;
uniform float uBeatPhase;
uniform float uExcitation;

varying vec3 vWorldPos;
varying vec3 vNormal;
varying vec3 vModelPos;
varying vec3 vBary;
varying vec2 vPlaneMeta;
varying float vBeat;

void main() {
  vModelPos = aPos;
  vBary = aBary;
  vPlaneMeta = aPlaneMeta;

  float waveDelay = clamp((0.8 - aPos.y) * 0.055, 0.0, 0.14);
  vBeat = max(0.0, lubDubWave(uBeatPhase - waveDelay, uExcitation));

  vec3 deformedPos = deformHeartPoint(aPos, uBeatPhase, uExcitation, uTime);

  // Rotate into world space
  vec4 rotPos = uRot * vec4(deformedPos, 1.0);
  vec4 rotNorm = uRot * vec4(aNormal, 0.0);

  vWorldPos = rotPos.xyz - vec3(0.0, 0.0, uCameraZ);
  vNormal = normalize(rotNorm.xyz);

  gl_Position = uProj * vec4(vWorldPos, 1.0);
}
`;

const HEART_FS = `
${GLSL_COMMON}
varying vec3 vWorldPos;
varying vec3 vNormal;
varying vec3 vModelPos;
varying vec3 vBary;
varying vec2 vPlaneMeta;
varying float vBeat;

uniform float uTime;
uniform float uBeatPhase;
uniform float uExcitation;

void main() {
  vec3 N = normalize(vNormal);
  vec3 V = normalize(-vWorldPos);

  // Studio 3-point lighting rig to accentuate individual flat geometric planes
  vec3 L1 = normalize(vec3(0.52, 0.78, 0.62));
  vec3 L2 = normalize(vec3(-0.68, -0.35, 0.55));
  vec3 H1 = normalize(L1 + V);

  float diff1 = max(dot(N, L1), 0.0);
  float diff2 = max(dot(N, L2), 0.0);
  float NdotV = max(dot(N, V), 0.0);
  float fresnel = pow(1.0 - NdotV, 2.6);

  // Barycentric distance to the nearest edge of this triangular plane
  float minBary = min(vBary.x, min(vBary.y, vBary.z));

  // Crisp 3D graph edge wireframe connecting the nodes + recessed flat polygon plane interior
  float edgeWire = 1.0 - smoothstep(0.0, 0.068, minBary);
  float innerBevel = smoothstep(0.068, 0.14, minBary);
  // Subtle circuit trace line inside each plane
  float innerCircuit = (1.0 - smoothstep(0.0, 0.018, abs(minBary - 0.16))) * 0.35;

  // Per-plane facet brightness variation so adjacent planes look unmistakably distinct
  float planeHash = vPlaneMeta.x;
  float facetShade = 0.72 + 0.56 * planeHash;

  // Webapp main theme colors (#39ff14 / #33ff33 neon terminal green & dark matrix emerald planes)
  vec3 darkEmeraldPlane = vec3(0.012, 0.065, 0.022);
  vec3 litEmeraldPlane  = vec3(0.055, 0.340, 0.095) * facetShade;
  vec3 neonThemeGreen   = vec3(0.224, 1.000, 0.078); // #39ff14
  vec3 brightMintGreen  = vec3(0.480, 1.000, 0.540);

  // Flat-shaded polygon plane interior
  vec3 planeCol = mix(darkEmeraldPlane, litEmeraldPlane, diff1 * 0.82 + diff2 * 0.25);
  planeCol *= (0.72 + 0.28 * innerBevel);

  // Flat-facet crisp specular reflection across individual planes
  float specFacet = pow(max(dot(N, H1), 0.0), 36.0) * 0.45 * innerBevel;
  planeCol += brightMintGreen * specFacet;

  // Heartbeat conduction wave traveling down the 3D graph planes & edges
  float conductionWave = 0.5 + 0.5 * sin(uBeatPhase * 6.2831853 - vModelPos.y * 5.2 + planeHash * 3.14);
  float planePulse = pow(conductionWave, 3.0) * (0.15 + 0.35 * vBeat + 0.35 * uExcitation);
  planeCol += neonThemeGreen * planePulse * (0.28 + 0.45 * planeHash) * innerBevel;

  // 3D Graph Edges connecting the heart's nodes
  float edgeEnergy = 0.48 + 0.42 * vBeat + 0.45 * uExcitation + 0.25 * conductionWave;
  vec3 edgeCol = mix(neonThemeGreen, brightMintGreen, 0.25 + 0.35 * vBeat) * edgeEnergy;

  // Combine flat polygon plane + inner bevel circuit + glowing 3D graph edge
  vec3 col = mix(planeCol, edgeCol, edgeWire);
  col += neonThemeGreen * innerCircuit * (0.25 + 0.35 * uExcitation);
  col += neonThemeGreen * fresnel * (0.28 + 0.24 * vBeat + 0.28 * uExcitation);

  gl_FragColor = vec4(col, 1.0);
}
`;

// 3. Chaotic 3D Neuron Tubes Shader (Rainbow Glow + Traveling Action Potential Light Pulses)
const TUBE_VS = `
${GLSL_COMMON}
attribute vec3 aPos;
attribute vec3 aCenter;
attribute vec3 aNormal;
attribute vec4 aMeta; // x: u in [0,1], y: hue, z: neuronId, w: baseRadius

uniform mat4 uProj;
uniform mat4 uRot;
uniform float uCameraZ;
uniform float uTime;
uniform float uBeatPhase;
uniform float uExcitation;
uniform vec2 uAngularVel;

varying vec3 vWorldPos;
varying vec3 vNormal;
varying float vU;
varying float vHue;
varying float vNeuronId;
varying float vPulseTotal;

// Compute localized, crisp traveling action potential light pulses along neuron u in [0, 1]
float computeActionPotentials(float u, float neuronId, float time, float beatPhase, float excitation) {
  // Stagger neuron firing phases so pulses look like distinct light packets racing along individual neurons
  float cardiacWavePos = fract(beatPhase - neuronId * 0.65);
  float dCardiac = u - cardiacWavePos;
  // Crisp leading edge + focused comet tail behind the pulse
  float pCardiac = exp(-pow(dCardiac * 26.0, 2.0)) +
                   step(dCardiac, 0.0) * exp(dCardiac * 16.0) * 0.55;
  pCardiac *= smoothstep(0.0, 0.06, cardiacWavePos) * (1.0 - 0.25 * cardiacWavePos);

  // Fast chaotic neuron firing train
  float speed1 = 0.48 + fract(neuronId * 7.13) * 0.42 + excitation * 0.35;
  float phase1 = fract(time * speed1 + neuronId * 17.37);
  float d1 = u - phase1;
  float p1 = exp(-pow(d1 * 30.0, 2.0)) + step(d1, 0.0) * exp(d1 * 18.0) * 0.50;

  // Secondary rapid burst train (primarily active when user interacts)
  float speed2 = 0.78 + fract(neuronId * 13.91) * 0.55 + excitation * 0.45;
  float phase2 = fract(time * speed2 + neuronId * 9.71 + 0.5);
  float d2 = u - phase2;
  float p2 = (exp(-pow(d2 * 32.0, 2.0)) + step(d2, 0.0) * exp(d2 * 20.0) * 0.48) * (0.20 + 0.85 * excitation);

  return pCardiac * (0.55 + 0.55 * excitation) + p1 * (0.48 + 0.52 * excitation) + p2;
}

void main() {
  float u = aMeta.x;
  float hue = aMeta.y;
  float neuronId = aMeta.z;
  float baseRadius = aMeta.w;

  vU = u;
  vHue = hue;
  vNeuronId = neuronId;

  // Compute traveling light pulse at this vertex
  float pulse = computeActionPotentials(u, neuronId, uTime, uBeatPhase, uExcitation);
  vPulseTotal = pulse;

  // Subtle physical tube swell as the light packet passes through
  float swell = 1.0 + clamp(pulse, 0.0, 1.5) * (0.28 + 0.24 * uExcitation);

  // Deform the spine center so at u=0 the neuron origin stays 100% locked to its 3D Heart Graph Node
  float delayedBeatPhase = uBeatPhase - u * 0.28;
  vec3 heartDeformedCenter = deformHeartPoint(aCenter, delayedBeatPhase, uExcitation, uTime);
  vec3 centerPos = mix(heartDeformedCenter, aCenter, smoothstep(0.0, 0.65, u));
  vec3 pos = centerPos + aNormal * (baseRadius * swell);

  // Add organic 3D chaotic swimming/undulation + Pixar follow-through from user rotation (zero at u=0 so origin stays anchored to node)
  float swayAmp = u * u * (0.060 + 0.050 * uExcitation);
  float freq = 2.2 + fract(neuronId * 9.3) * 1.6;
  vec3 organicSway = vec3(
    sin(uTime * freq + u * 5.5 + neuronId * 20.0),
    cos(uTime * (freq * 0.9) - u * 4.8 + neuronId * 15.0),
    sin(uTime * (freq * 1.1) + u * 6.2 - neuronId * 12.0)
  ) * swayAmp;

  // Follow-through inertia when the user grabs & spins the heart
  vec3 inertiaSway = vec3(-uAngularVel.y, uAngularVel.x, (uAngularVel.x - uAngularVel.y) * 0.5) * (u * u * 0.14);

  // Subtle outward ripple from the heartbeat wave
  float beatRipple = lubDubWave(delayedBeatPhase, uExcitation) * u * (1.0 - u * 0.4) * 0.075;
  pos += organicSway + inertiaSway + normalize(aCenter + vec3(0.0, 0.001, 0.0)) * beatRipple;

  vec4 rotPos = uRot * vec4(pos, 1.0);
  vec4 rotNorm = uRot * vec4(aNormal, 0.0);

  vWorldPos = rotPos.xyz - vec3(0.0, 0.0, uCameraZ);
  vNormal = normalize(rotNorm.xyz);

  gl_Position = uProj * vec4(vWorldPos, 1.0);
}
`;

const TUBE_FS = `
${GLSL_COMMON}
varying vec3 vWorldPos;
varying vec3 vNormal;
varying float vU;
varying float vHue;
varying float vNeuronId;
varying float vPulseTotal;

uniform float uTime;
uniform float uExcitation;

void main() {
  vec3 N = normalize(vNormal);
  vec3 V = normalize(-vWorldPos);

  // Cylindrical fiber-optic core vs rim
  float NdotV = abs(dot(N, V));
  float coreFilament = pow(NdotV, 1.8);
  float glassRim = pow(1.0 - NdotV, 2.4);

  // Dynamic rainbow color shifting along the neuron and over time
  float dynamicHue = fract(vHue + vU * 0.38 - uTime * (0.08 + 0.14 * uExcitation));
  vec3 rainbowCol = rainbowPalette(dynamicHue);
  vec3 secondaryRainbow = rainbowPalette(fract(dynamicHue + 0.18));

  // Smooth, luminous rainbow filament base
  float baseGlow = (0.34 + 0.28 * uExcitation) * (1.0 - 0.18 * vU);
  vec3 baseCol = rainbowCol * (0.52 + 0.48 * coreFilament + 0.38 * glassRim) * baseGlow;

  // High-frequency plasma shimmer inside active light pulses
  float plasma = 0.65 + 0.35 * sin(vU * 38.0 - uTime * 12.0 + vNeuronId * 30.0);
  float pulse = vPulseTotal * plasma;

  // Traveling light pulse color: vivid saturated rainbow light + focused bright core only at pulse peak
  vec3 pulseColor = mix(rainbowCol, secondaryRainbow, 0.32) * pulse * (0.88 + 0.52 * uExcitation);
  vec3 whiteHotCore = mix(rainbowCol, vec3(1.0, 0.96, 1.0), 0.58) *
                      pow(max(pulse - 0.55, 0.0), 2.0) * coreFilament * (0.48 + 0.50 * uExcitation);

  // Seamless transition at the 3D Heart Graph Node origin (u < 0.09) from theme neon-green into the rainbow neuron
  vec3 heartRootTint = vec3(0.224, 1.000, 0.078);
  vec3 finalCol = baseCol + pulseColor + whiteHotCore;
  finalCol = mix(heartRootTint * (0.55 + pulse * 0.45), finalCol, smoothstep(0.0, 0.09, vU));

  gl_FragColor = vec4(finalCol, 1.0);
}
`;

// 4. 3D Heart Graph Nodes, Synaptic Bulbs (Boutons) & Floating 3D Bioluminescent Spores Shader
const PARTICLE_VS = `
${GLSL_COMMON}
attribute vec3 aPos;
attribute vec4 aMeta; // x: u/phase, y: hue, z: neuronId/seed, w: size (>100 heart graph node, >0 synaptic node, <0 ambient spore)

uniform mat4 uProj;
uniform mat4 uRot;
uniform float uCameraZ;
uniform float uTime;
uniform float uBeatPhase;
uniform float uExcitation;
uniform vec2 uAngularVel;
uniform float uPixelRatio;

varying vec3 vColor;
varying float vIntensity;
varying float vNodeType; // 2.0 = 3D heart graph node, 1.0 = neuron synapse, 0.0 = ambient spore

float computeActionPotentials(float u, float neuronId, float time, float beatPhase, float excitation) {
  float cardiacWavePos = fract(beatPhase - neuronId * 0.65);
  float dCardiac = u - cardiacWavePos;
  float pCardiac = exp(-pow(dCardiac * 20.0, 2.0));

  float speed1 = 0.48 + fract(neuronId * 7.13) * 0.42 + excitation * 0.35;
  float phase1 = fract(time * speed1 + neuronId * 17.37);
  float p1 = exp(-pow((u - phase1) * 22.0, 2.0));

  float speed2 = 0.78 + fract(neuronId * 13.91) * 0.55 + excitation * 0.45;
  float phase2 = fract(time * speed2 + neuronId * 9.71 + 0.5);
  float p2 = exp(-pow((u - phase2) * 24.0, 2.0)) * (0.20 + 0.85 * excitation);

  return pCardiac * (0.65 + 0.6 * excitation) + p1 * (0.55 + 0.55 * excitation) + p2;
}

void main() {
  float u = aMeta.x;
  float hue = aMeta.y;
  float seed = aMeta.z;
  float rawSize = aMeta.w;

  vec3 pos = aPos;
  float pulse = 0.0;
  float ptSize = abs(rawSize);

  if (rawSize > 100.0) {
    // 3D Heart Graph Node (vertices of the anatomical planar heart graph where neurons originate)
    vNodeType = 2.0;
    ptSize = rawSize - 100.0;
    float waveDelay = clamp((0.8 - pos.y) * 0.055, 0.0, 0.14);
    pulse = max(0.0, lubDubWave(uBeatPhase - waveDelay, uExcitation));
    pos = deformHeartPoint(pos, uBeatPhase, uExcitation, uTime);

    bool isNeuronOriginHub = hue > 0.5;
    vColor = isNeuronOriginHub ? vec3(0.38, 1.0, 0.28) : vec3(0.224, 1.0, 0.078);
    vIntensity = (isNeuronOriginHub ? 0.52 : 0.30) + pulse * 0.50 + uExcitation * 0.32;
  } else if (rawSize > 0.0) {
    // Attached Synaptic Terminal / Bouton on a chaotic rainbow neuron
    vNodeType = 1.0;
    pulse = computeActionPotentials(u, seed, uTime, uBeatPhase, uExcitation);

    float delayedBeatPhase = uBeatPhase - u * 0.28;
    vec3 heartDeformed = deformHeartPoint(pos, delayedBeatPhase, uExcitation, uTime);
    pos = mix(heartDeformed, pos, smoothstep(0.0, 0.65, u));

    float swayAmp = u * u * (0.060 + 0.050 * uExcitation);
    float freq = 2.2 + fract(seed * 9.3) * 1.6;
    vec3 organicSway = vec3(
      sin(uTime * freq + u * 5.5 + seed * 20.0),
      cos(uTime * (freq * 0.9) - u * 4.8 + seed * 15.0),
      sin(uTime * (freq * 1.1) + u * 6.2 - seed * 12.0)
    ) * swayAmp;
    vec3 inertiaSway = vec3(-uAngularVel.y, uAngularVel.x, (uAngularVel.x - uAngularVel.y) * 0.5) * (u * u * 0.14);
    float beatRipple = lubDubWave(delayedBeatPhase, uExcitation) * u * (1.0 - u * 0.4) * 0.075;
    pos += organicSway + inertiaSway + normalize(aPos + vec3(0.0, 0.001, 0.0)) * beatRipple;

    float dynamicHue = fract(hue + u * 0.38 - uTime * (0.08 + 0.14 * uExcitation));
    vColor = rainbowPalette(dynamicHue);
    vIntensity = 0.22 + pulse * 0.75 + uExcitation * 0.22;
  } else {
    // Floating 3D Bioluminescent Spore / Bokeh Particle
    vNodeType = 0.0;
    float driftSpeed = 0.35 + 0.45 * uExcitation;
    pos += vec3(
      sin(uTime * driftSpeed + seed * 18.0 + u * 6.28) * 0.14,
      cos(uTime * (driftSpeed * 0.85) + u * 12.0) * 0.14,
      sin(uTime * (driftSpeed * 1.15) - seed * 14.0) * 0.14
    );
    pulse = 0.35 + 0.65 * pow(0.5 + 0.5 * sin(uTime * (1.6 + 2.0 * uExcitation) + u * 25.0), 2.0);

    float dynamicHue = fract(hue + u * 0.38 - uTime * (0.08 + 0.14 * uExcitation));
    vColor = rainbowPalette(dynamicHue);
    vIntensity = 0.14 + pulse * 0.26 + uExcitation * 0.16;
  }

  vec4 rotPos = uRot * vec4(pos, 1.0);
  vec3 worldPos = rotPos.xyz - vec3(0.0, 0.0, uCameraZ);
  gl_Position = uProj * vec4(worldPos, 1.0);

  float dist = max(length(worldPos), 1.5);
  float basePtSize = ptSize * (1.0 + pulse * (vNodeType > 0.5 ? 0.45 : 0.25) + uExcitation * 0.25);
  gl_PointSize = clamp((basePtSize * uPixelRatio * 2.4) / dist, 1.5, 26.0);
}
`;

const PARTICLE_FS = `
precision highp float;
varying vec3 vColor;
varying float vIntensity;
varying float vNodeType;

void main() {
  vec2 coord = gl_PointCoord * 2.0 - 1.0;
  float r2 = dot(coord, coord);
  if (r2 > 1.0) discard;

  float r = sqrt(r2);
  // Crisp jewel-like bioluminescent node with soft halo
  float halo = exp(-r2 * (vNodeType > 1.5 ? 6.5 : 5.2));
  float core = exp(-r2 * 22.0);
  float softEdge = 1.0 - smoothstep(0.55, 1.0, r);

  vec3 col = vColor * halo * vIntensity + mix(vColor, vec3(1.0), 0.68) * core * (vIntensity * (vNodeType > 0.5 ? 0.72 : 0.35));
  gl_FragColor = vec4(col * softEdge, softEdge);
}
`;

// 5. Post-Processing Bloom & ACES Filmic Tone Mapping Shaders
const POST_VS = `
attribute vec2 aQuadPos;
varying vec2 vUV;
void main() {
  vUV = aQuadPos * 0.5 + 0.5;
  gl_Position = vec4(aQuadPos, 0.0, 1.0);
}
`;

const BLUR_FS = `
precision highp float;
varying vec2 vUV;
uniform sampler2D uTex;
uniform vec2 uDirection;
uniform bool uBrightPass;

void main() {
  vec2 off1 = vec2(1.3846153846) * uDirection;
  vec2 off2 = vec2(3.2307692308) * uDirection;

  vec3 c0 = texture2D(uTex, vUV).rgb;
  vec3 c1 = texture2D(uTex, vUV + off1).rgb;
  vec3 c2 = texture2D(uTex, vUV - off1).rgb;
  vec3 c3 = texture2D(uTex, vUV + off2).rgb;
  vec3 c4 = texture2D(uTex, vUV - off2).rgb;

  if (uBrightPass) {
    // Only extract true high-energy peaks (firing pulses & bright graph nodes) so the scene never washes out
    c0 *= smoothstep(0.56, 0.98, max(c0.r, max(c0.g, c0.b)));
    c1 *= smoothstep(0.56, 0.98, max(c1.r, max(c1.g, c1.b)));
    c2 *= smoothstep(0.56, 0.98, max(c2.r, max(c2.g, c2.b)));
    c3 *= smoothstep(0.56, 0.98, max(c3.r, max(c3.g, c3.b)));
    c4 *= smoothstep(0.56, 0.98, max(c4.r, max(c4.g, c4.b)));
  }

  vec3 sum = c0 * 0.2270270270
           + (c1 + c2) * 0.3162162162
           + (c3 + c4) * 0.0702702703;
  gl_FragColor = vec4(sum, 1.0);
}
`;

const COMPOSITE_FS = `
precision highp float;
varying vec2 vUV;
uniform sampler2D uSceneTex;
uniform sampler2D uBloomTex;
uniform float uExcitation;
uniform float uBeatPulse;

// ACES Filmic Tone Mapping for cinematic Pixar dynamic range
vec3 acesToneMap(vec3 x) {
  float a = 2.51;
  float b = 0.03;
  float c = 2.43;
  float d = 0.59;
  float e = 0.14;
  return clamp((x * (a * x + b)) / (x * (c * x + d) + e), 0.0, 1.0);
}

void main() {
  vec2 centered = vUV - 0.5;
  float dist = length(centered);

  // Subtle futuristic chromatic dispersion near edges during intense interaction
  float caOffset = dist * (0.0012 + 0.0018 * uExcitation + 0.0010 * uBeatPulse);
  vec2 dir = normalize(centered + vec2(1e-5));

  vec3 scene;
  scene.r = texture2D(uSceneTex, vUV + dir * caOffset).r;
  scene.g = texture2D(uSceneTex, vUV).g;
  scene.b = texture2D(uSceneTex, vUV - dir * caOffset).b;

  vec3 bloom = texture2D(uBloomTex, vUV).rgb;

  // Combine crisp 3D scene with refined, subtle optical bloom
  vec3 hdr = scene + bloom * (0.40 + 0.25 * uExcitation + 0.10 * uBeatPulse);

  // Filmic tone mapping + natural contrast
  vec3 mapped = acesToneMap(hdr * 0.96);
  mapped = pow(mapped, vec3(0.98));

  // Soft cinematic vignette
  float vignette = 1.0 - smoothstep(0.45, 0.98, dist) * 0.42;
  mapped *= vignette;

  gl_FragColor = vec4(mapped, 1.0);
}
`;

export default function NeuralHeartSynapse({ onInteract }: NeuralHeartSynapseProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hudBpm, setHudBpm] = useState(54);
  const [isExcited, setIsExcited] = useState(false);
  const [isGrabbing, setIsGrabbing] = useState(false);

  const onInteractRef = useRef(onInteract);
  useEffect(() => {
    onInteractRef.current = onInteract;
  }, [onInteract]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = (canvas.getContext('webgl', {
      alpha: false,
      antialias: true,
      powerPreference: 'high-performance',
    }) || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;

    if (!gl) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Compile all shader programs
    const bgProg = createProgram(gl, BG_VS, BG_FS);
    const heartProg = createProgram(gl, HEART_VS, HEART_FS);
    const tubeProg = createProgram(gl, TUBE_VS, TUBE_FS);
    const particleProg = createProgram(gl, PARTICLE_VS, PARTICLE_FS);
    const blurProg = createProgram(gl, POST_VS, BLUR_FS);
    const compositeProg = createProgram(gl, POST_VS, COMPOSITE_FS);

    // Fullscreen quad buffer
    const quadBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW
    );

    // Build 3D Anatomical Human Heart Graph (flat triangular planes + connected 3D graph nodes)
    const heartGraph = buildAnatomicalHeartGraph();
    const heartVbo = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, heartVbo);
    gl.bufferData(gl.ARRAY_BUFFER, heartGraph.verts, gl.STATIC_DRAW);
    const heartVertexCount = heartGraph.vertexCount;

    // Build Chaotic 3D Rainbow Neurons originating directly out of the 3D Heart Graph Nodes
    const neuronsMesh = buildChaoticNeuronsMesh(heartGraph.graphNodes);
    const tubeVbo = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, tubeVbo);
    gl.bufferData(gl.ARRAY_BUFFER, neuronsMesh.verts, gl.STATIC_DRAW);

    const tubeIbo = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, tubeIbo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, neuronsMesh.indices, gl.STATIC_DRAW);
    const tubeIndexCount = neuronsMesh.indices.length;

    // Build 3D Heart Graph Node Hubs + Synaptic Bulbs + Ambient Bioluminescent Spores
    const particlesData = buildParticlesBuffer(neuronsMesh.synapticNodes, heartGraph.graphNodes);
    const particleVbo = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, particleVbo);
    gl.bufferData(gl.ARRAY_BUFFER, particlesData.data, gl.STATIC_DRAW);
    const particleCount = particlesData.count;

    // Offscreen Framebuffers for Multi-Pass Optical Bloom
    interface FBO {
      fbo: WebGLFramebuffer;
      tex: WebGLTexture;
      depth?: WebGLRenderbuffer;
      width: number;
      height: number;
    }

    let sceneFbo: FBO | null = null;
    let bloomFboA: FBO | null = null;
    let bloomFboB: FBO | null = null;

    const createFBO = (w: number, h: number, withDepth: boolean): FBO | null => {
      const fbo = gl.createFramebuffer();
      const tex = gl.createTexture();
      if (!fbo || !tex) return null;

      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);

      let depth: WebGLRenderbuffer | undefined;
      if (withDepth) {
        const rb = gl.createRenderbuffer();
        if (rb) {
          depth = rb;
          gl.bindRenderbuffer(gl.RENDERBUFFER, rb);
          gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, w, h);
          gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, rb);
        }
      }

      const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      if (status !== gl.FRAMEBUFFER_COMPLETE) {
        return null;
      }
      return { fbo, tex, depth, width: w, height: h };
    };

    const deleteFBO = (target: FBO | null) => {
      if (!target) return;
      gl.deleteFramebuffer(target.fbo);
      gl.deleteTexture(target.tex);
      if (target.depth) gl.deleteRenderbuffer(target.depth);
    };

    let width = 1;
    let height = 1;
    let dpr = 1;

    const rebuildFBOs = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      const nextW = Math.max(1, Math.floor(rect.width * dpr));
      const nextH = Math.max(1, Math.floor(rect.height * dpr));

      if (nextW === width && nextH === height && sceneFbo) return;
      width = nextW;
      height = nextH;
      canvas.width = width;
      canvas.height = height;

      deleteFBO(sceneFbo);
      deleteFBO(bloomFboA);
      deleteFBO(bloomFboB);

      const bloomW = Math.max(1, Math.floor(width * 0.5));
      const bloomH = Math.max(1, Math.floor(height * 0.5));

      sceneFbo = createFBO(width, height, true);
      bloomFboA = createFBO(bloomW, bloomH, false);
      bloomFboB = createFBO(bloomW, bloomH, false);
    };

    rebuildFBOs();
    const resizeObserver = new ResizeObserver(() => rebuildFBOs());
    resizeObserver.observe(canvas);

    // --- Interactive 3D State (Unconstrained 360-Degree Quaternion Trackball) ---
    // Start deeper in space (cameraZ = 8.6) so the entire anatomical heart and its radiating chaotic neural tips float in deep space
    let orientation: Quat = quatNormalize([-0.08, 0.18, 0.02, 0.98]);
    let angularVelX = 0; // pitch rate
    let angularVelY = 0; // yaw rate
    let cameraZ = 8.6;
    let targetCameraZ = 8.6;

    let isDragging = false;
    let lastPointerX = 0;
    let lastPointerY = 0;

    // Cardiac & Neural Excitation State
    let beatPhase = 0;
    let excitation = 0;
    let targetExcitation = 0;
    let interactionHoldBoost = 0;

    const triggerUserExcitation = (boost: number, notifyInteract = true) => {
      targetExcitation = Math.min(1.0, Math.max(targetExcitation, boost));
      if (notifyInteract && onInteractRef.current) {
        onInteractRef.current();
      }
    };

    const handlePointerDown = (e: PointerEvent) => {
      isDragging = true;
      setIsGrabbing(true);
      lastPointerX = e.clientX;
      lastPointerY = e.clientY;
      interactionHoldBoost = 1.0;
      triggerUserExcitation(1.0, true);
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        // Ignore if capture unsupported
      }
    };

    const handlePointerMove = (e: PointerEvent) => {
      if (!isDragging) {
        // Gentle hover excitation when moving cursor over the neural heart
        triggerUserExcitation(0.42, false);
        return;
      }

      const dx = e.clientX - lastPointerX;
      const dy = e.clientY - lastPointerY;
      lastPointerX = e.clientX;
      lastPointerY = e.clientY;

      const sensitivity = 0.0072;
      const yawDelta = dx * sensitivity;
      const pitchDelta = dy * sensitivity;

      // Apply screen-space yaw (around world Y) and pitch (around world X) to quaternion
      // This enables full 360° unconstrained rotation from every possible angle without gimbal lock!
      const qYaw = quatFromAxisAngle([0, 1, 0], yawDelta);
      const qPitch = quatFromAxisAngle([1, 0, 0], pitchDelta);
      orientation = quatNormalize(quatMultiply(qYaw, quatMultiply(qPitch, orientation)));

      angularVelY = dx * 0.28;
      angularVelX = dy * 0.28;

      const dragSpeed = Math.min(1.0, Math.hypot(dx, dy) * 0.045);
      interactionHoldBoost = 1.0;
      triggerUserExcitation(Math.min(1.0, 0.82 + dragSpeed * 0.18));
    };

    const handlePointerUp = (e: PointerEvent) => {
      if (!isDragging) return;
      isDragging = false;
      setIsGrabbing(false);
      interactionHoldBoost = 0;
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        // Ignore
      }
    };

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      targetCameraZ = Math.max(3.4, Math.min(13.5, targetCameraZ + e.deltaY * 0.0032));
      triggerUserExcitation(0.78);
    };

    canvas.addEventListener('pointerdown', handlePointerDown);
    canvas.addEventListener('pointermove', handlePointerMove);
    canvas.addEventListener('pointerup', handlePointerUp);
    canvas.addEventListener('pointercancel', handlePointerUp);
    canvas.addEventListener('wheel', handleWheel, { passive: false });

    // --- Render Loop ---
    let animId = 0;
    let lastTime = performance.now();
    let elapsed = 0;
    let lastHudUpdate = 0;

    const drawQuad = (prog: WebGLProgram) => {
      gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
      const loc = gl.getAttribLocation(prog, 'aQuadPos');
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    };

    const render = (now: number) => {
      const dt = Math.min(0.05, Math.max(0.001, (now - lastTime) * 0.001));
      lastTime = now;
      elapsed += dt;

      // Smoothly evolve neural/cardiac excitation
      if (isDragging || interactionHoldBoost > 0) {
        targetExcitation = Math.min(1.0, Math.max(targetExcitation, 0.92));
      } else {
        targetExcitation = Math.max(0.0, targetExcitation - dt * 0.34);
      }
      excitation += (targetExcitation - excitation) * Math.min(1.0, dt * 5.5);

      // Update rotational inertia & gentle idle orbit
      if (!isDragging) {
        const damping = Math.exp(-dt * 4.2);
        angularVelX *= damping;
        angularVelY *= damping;

        const idleSpin = prefersReducedMotion ? 0.0 : 0.22 * (1.0 + excitation * 0.65);
        const totalYaw = (angularVelY + idleSpin) * dt;
        const totalPitch = angularVelX * dt;

        const qYaw = quatFromAxisAngle([0, 1, 0], totalYaw);
        const qPitch = quatFromAxisAngle([1, 0, 0], totalPitch);
        orientation = quatNormalize(quatMultiply(qYaw, quatMultiply(qPitch, orientation)));
      } else {
        // Decay angular velocity slightly if pointer is held still
        angularVelX *= Math.exp(-dt * 10.0);
        angularVelY *= Math.exp(-dt * 10.0);
      }

      cameraZ += (targetCameraZ - cameraZ) * Math.min(1.0, dt * 8.0);

      // Heartbeat BPM: slow 54 BPM at rest -> intense 128 BPM when user interacts
      const baseBpm = prefersReducedMotion ? 45 : 54;
      const maxBpm = prefersReducedMotion ? 78 : 128;
      const currentBpm = baseBpm + excitation * (maxBpm - baseBpm);
      beatPhase = (beatPhase + dt * (currentBpm / 60.0)) % 1.0;

      // Update React HUD state throttled to ~5Hz
      if (now - lastHudUpdate > 180) {
        lastHudUpdate = now;
        setHudBpm(Math.round(currentBpm));
        setIsExcited(excitation > 0.25);
      }

      const projMat = mat4Perspective((46 * Math.PI) / 180, width / Math.max(height, 1), 0.1, 30.0);
      const rotMat = quatToMat4(orientation);

      // Compute instantaneous beat pulse scalar for post-FX
      const p = beatPhase;
      const lub = Math.exp(-Math.pow((p - 0.11) / 0.048, 2.0));
      const dub = 1.18 * Math.exp(-Math.pow((p - 0.25) / 0.062, 2.0));
      const beatPulse = Math.max(0.0, (lub + dub) * (1.0 + excitation * 0.85));

      const useBloomFbo = Boolean(sceneFbo && bloomFboA && bloomFboB);

      // --- PASS 1: Render 3D Scene (into sceneFbo or directly to screen) ---
      if (useBloomFbo && sceneFbo) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, sceneFbo.fbo);
      } else {
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      }
      gl.viewport(0, 0, width, height);
      gl.clearColor(0.004, 0.01, 0.006, 1.0);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

      // 1A. Background Nebula
      gl.disable(gl.DEPTH_TEST);
      gl.disable(gl.BLEND);
      gl.useProgram(bgProg);
      gl.uniform1f(gl.getUniformLocation(bgProg, 'uTime'), elapsed);
      gl.uniform1f(gl.getUniformLocation(bgProg, 'uBeatPhase'), beatPhase);
      gl.uniform1f(gl.getUniformLocation(bgProg, 'uExcitation'), excitation);
      gl.uniform2f(gl.getUniformLocation(bgProg, 'uResolution'), width, height);
      drawQuad(bgProg);

      // 1B. Central 3D Anatomical Human Heart Graph (Flat Planes + Neon Green 3D Graph Edges)
      gl.enable(gl.DEPTH_TEST);
      gl.depthFunc(gl.LEQUAL);
      gl.depthMask(true);
      gl.useProgram(heartProg);
      gl.uniformMatrix4fv(gl.getUniformLocation(heartProg, 'uProj'), false, projMat);
      gl.uniformMatrix4fv(gl.getUniformLocation(heartProg, 'uRot'), false, rotMat);
      gl.uniform1f(gl.getUniformLocation(heartProg, 'uCameraZ'), cameraZ);
      gl.uniform1f(gl.getUniformLocation(heartProg, 'uTime'), elapsed);
      gl.uniform1f(gl.getUniformLocation(heartProg, 'uBeatPhase'), beatPhase);
      gl.uniform1f(gl.getUniformLocation(heartProg, 'uExcitation'), excitation);

      gl.bindBuffer(gl.ARRAY_BUFFER, heartVbo);
      const hPosLoc = gl.getAttribLocation(heartProg, 'aPos');
      const hNormLoc = gl.getAttribLocation(heartProg, 'aNormal');
      const hBaryLoc = gl.getAttribLocation(heartProg, 'aBary');
      const hPlaneMetaLoc = gl.getAttribLocation(heartProg, 'aPlaneMeta');
      gl.enableVertexAttribArray(hPosLoc);
      gl.enableVertexAttribArray(hNormLoc);
      gl.enableVertexAttribArray(hBaryLoc);
      gl.enableVertexAttribArray(hPlaneMetaLoc);
      const heartStride = 11 * 4;
      gl.vertexAttribPointer(hPosLoc, 3, gl.FLOAT, false, heartStride, 0);
      gl.vertexAttribPointer(hNormLoc, 3, gl.FLOAT, false, heartStride, 12);
      gl.vertexAttribPointer(hBaryLoc, 3, gl.FLOAT, false, heartStride, 24);
      gl.vertexAttribPointer(hPlaneMetaLoc, 2, gl.FLOAT, false, heartStride, 36);

      gl.drawArrays(gl.TRIANGLES, 0, heartVertexCount);

      // 1C. Several Dozen Chaotic 3D Rainbow Neurons Originating from the Heart Graph Nodes
      gl.useProgram(tubeProg);
      gl.uniformMatrix4fv(gl.getUniformLocation(tubeProg, 'uProj'), false, projMat);
      gl.uniformMatrix4fv(gl.getUniformLocation(tubeProg, 'uRot'), false, rotMat);
      gl.uniform1f(gl.getUniformLocation(tubeProg, 'uCameraZ'), cameraZ);
      gl.uniform1f(gl.getUniformLocation(tubeProg, 'uTime'), elapsed);
      gl.uniform1f(gl.getUniformLocation(tubeProg, 'uBeatPhase'), beatPhase);
      gl.uniform1f(gl.getUniformLocation(tubeProg, 'uExcitation'), excitation);
      gl.uniform2f(
        gl.getUniformLocation(tubeProg, 'uAngularVel'),
        Math.max(-2.5, Math.min(2.5, angularVelX)),
        Math.max(-2.5, Math.min(2.5, angularVelY))
      );

      gl.bindBuffer(gl.ARRAY_BUFFER, tubeVbo);
      const tPosLoc = gl.getAttribLocation(tubeProg, 'aPos');
      const tCenterLoc = gl.getAttribLocation(tubeProg, 'aCenter');
      const tNormLoc = gl.getAttribLocation(tubeProg, 'aNormal');
      const tMetaLoc = gl.getAttribLocation(tubeProg, 'aMeta');
      gl.enableVertexAttribArray(tPosLoc);
      gl.enableVertexAttribArray(tCenterLoc);
      gl.enableVertexAttribArray(tNormLoc);
      gl.enableVertexAttribArray(tMetaLoc);
      const tubeStride = 13 * 4;
      gl.vertexAttribPointer(tPosLoc, 3, gl.FLOAT, false, tubeStride, 0);
      gl.vertexAttribPointer(tCenterLoc, 3, gl.FLOAT, false, tubeStride, 12);
      gl.vertexAttribPointer(tNormLoc, 3, gl.FLOAT, false, tubeStride, 24);
      gl.vertexAttribPointer(tMetaLoc, 4, gl.FLOAT, false, tubeStride, 36);

      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, tubeIbo);
      gl.drawElements(gl.TRIANGLES, tubeIndexCount, gl.UNSIGNED_SHORT, 0);

      // 1D. Additive 3D Heart Graph Node Hubs, Synaptic Bulbs & Floating Spores
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
      gl.depthMask(false);

      gl.useProgram(particleProg);
      gl.uniformMatrix4fv(gl.getUniformLocation(particleProg, 'uProj'), false, projMat);
      gl.uniformMatrix4fv(gl.getUniformLocation(particleProg, 'uRot'), false, rotMat);
      gl.uniform1f(gl.getUniformLocation(particleProg, 'uCameraZ'), cameraZ);
      gl.uniform1f(gl.getUniformLocation(particleProg, 'uTime'), elapsed);
      gl.uniform1f(gl.getUniformLocation(particleProg, 'uBeatPhase'), beatPhase);
      gl.uniform1f(gl.getUniformLocation(particleProg, 'uExcitation'), excitation);
      gl.uniform1f(gl.getUniformLocation(particleProg, 'uPixelRatio'), dpr);
      gl.uniform2f(
        gl.getUniformLocation(particleProg, 'uAngularVel'),
        Math.max(-2.5, Math.min(2.5, angularVelX)),
        Math.max(-2.5, Math.min(2.5, angularVelY))
      );

      gl.bindBuffer(gl.ARRAY_BUFFER, particleVbo);
      const pPosLoc = gl.getAttribLocation(particleProg, 'aPos');
      const pMetaLoc = gl.getAttribLocation(particleProg, 'aMeta');
      gl.enableVertexAttribArray(pPosLoc);
      gl.enableVertexAttribArray(pMetaLoc);
      gl.vertexAttribPointer(pPosLoc, 3, gl.FLOAT, false, 28, 0);
      gl.vertexAttribPointer(pMetaLoc, 4, gl.FLOAT, false, 28, 12);
      gl.drawArrays(gl.POINTS, 0, particleCount);

      gl.depthMask(true);
      gl.disable(gl.BLEND);
      gl.disable(gl.DEPTH_TEST);

      // --- PASS 2 & 3: Multi-Pass Optical Gaussian Bloom + ACES Filmic Composite ---
      if (useBloomFbo && sceneFbo && bloomFboA && bloomFboB) {
        const bw = bloomFboA.width;
        const bh = bloomFboA.height;

        gl.useProgram(blurProg);
        gl.uniform1i(gl.getUniformLocation(blurProg, 'uTex'), 0);

        // Pass 2A: Bright-pass + Horizontal Blur (sceneFbo -> bloomFboA)
        gl.bindFramebuffer(gl.FRAMEBUFFER, bloomFboA.fbo);
        gl.viewport(0, 0, bw, bh);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, sceneFbo.tex);
        gl.uniform2f(gl.getUniformLocation(blurProg, 'uDirection'), 1.0 / bw, 0.0);
        gl.uniform1i(gl.getUniformLocation(blurProg, 'uBrightPass'), 1);
        drawQuad(blurProg);

        // Pass 2B: Vertical Blur (bloomFboA -> bloomFboB)
        gl.bindFramebuffer(gl.FRAMEBUFFER, bloomFboB.fbo);
        gl.bindTexture(gl.TEXTURE_2D, bloomFboA.tex);
        gl.uniform2f(gl.getUniformLocation(blurProg, 'uDirection'), 0.0, 1.0 / bh);
        gl.uniform1i(gl.getUniformLocation(blurProg, 'uBrightPass'), 0);
        drawQuad(blurProg);

        // Pass 2C: Second Wide Blur Iteration (bloomFboB -> bloomFboA -> bloomFboB) for dreamy Pixar glow
        gl.bindFramebuffer(gl.FRAMEBUFFER, bloomFboA.fbo);
        gl.bindTexture(gl.TEXTURE_2D, bloomFboB.tex);
        gl.uniform2f(gl.getUniformLocation(blurProg, 'uDirection'), 2.1 / bw, 0.0);
        drawQuad(blurProg);

        gl.bindFramebuffer(gl.FRAMEBUFFER, bloomFboB.fbo);
        gl.bindTexture(gl.TEXTURE_2D, bloomFboA.tex);
        gl.uniform2f(gl.getUniformLocation(blurProg, 'uDirection'), 0.0, 2.1 / bh);
        drawQuad(blurProg);

        // Pass 3: Final Composite to Screen
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, width, height);
        gl.useProgram(compositeProg);

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, sceneFbo.tex);
        gl.uniform1i(gl.getUniformLocation(compositeProg, 'uSceneTex'), 0);

        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, bloomFboB.tex);
        gl.uniform1i(gl.getUniformLocation(compositeProg, 'uBloomTex'), 1);

        gl.uniform1f(gl.getUniformLocation(compositeProg, 'uExcitation'), excitation);
        gl.uniform1f(gl.getUniformLocation(compositeProg, 'uBeatPulse'), beatPulse);
        drawQuad(compositeProg);
      }

      animId = requestAnimationFrame(render);
    };

    animId = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animId);
      resizeObserver.disconnect();
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('pointerup', handlePointerUp);
      canvas.removeEventListener('pointercancel', handlePointerUp);
      canvas.removeEventListener('wheel', handleWheel);

      deleteFBO(sceneFbo);
      deleteFBO(bloomFboA);
      deleteFBO(bloomFboB);
      gl.deleteBuffer(quadBuffer);
      gl.deleteBuffer(heartVbo);
      gl.deleteBuffer(tubeVbo);
      gl.deleteBuffer(tubeIbo);
      gl.deleteBuffer(particleVbo);
      gl.deleteProgram(bgProg);
      gl.deleteProgram(heartProg);
      gl.deleteProgram(tubeProg);
      gl.deleteProgram(particleProg);
      gl.deleteProgram(blurProg);
      gl.deleteProgram(compositeProg);
    };
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius: 'inherit',
        zIndex: 0,
      }}
    >
      <canvas
        ref={canvasRef}
        aria-label="Interactive 3D Anatomical Human Heart Graph & Neural Synapse Animation — Drag to rotate in 3D"
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          cursor: isGrabbing ? 'grabbing' : 'grab',
          touchAction: 'none',
          userSelect: 'none',
        }}
      />

      {/* Subtle Futuristic Telemetry HUD in Webapp Theme Neon Green */}
      <div
        style={{
          position: 'absolute',
          bottom: '10px',
          left: '14px',
          right: '14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pointerEvents: 'none',
          userSelect: 'none',
          fontSize: '0.73rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: isExcited ? '#80ff66' : 'rgba(57, 255, 20, 0.75)',
          textShadow: isExcited
            ? '0 0 10px rgba(57, 255, 20, 0.8)'
            : '0 0 6px rgba(57, 255, 20, 0.35)',
          transition: 'color 0.3s ease, text-shadow 0.3s ease',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: isExcited
                ? 'linear-gradient(135deg, #39ff14, #00f2fe)'
                : '#39ff14',
              boxShadow: isExcited
                ? '0 0 12px #39ff14, 0 0 20px #00f2fe'
                : '0 0 8px #39ff14',
              display: 'inline-block',
            }}
          />
          <span>
            CARDIAC GRAPH: <strong>{hudBpm} BPM</strong> • {isExcited ? 'HYPER-SYNAPTIC BURST' : 'RESTING RHYTHM'}
          </span>
        </span>
        <span style={{ opacity: 0.85 }}>
          [GRAB &amp; ROTATE 360° • SCROLL TO ZOOM]
        </span>
      </div>
    </div>
  );
}
